import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  FileText,
  ChevronDown,
  ChevronUp,
  Video,
  File
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_MODULE = { title: "", description: "", order: 0, is_active: true };
const EMPTY_LESSON = {
  module_id: "",
  title: "",
  description: "",
  video_url: "",
  documents: [],
  order: 0,
  is_active: true
};

const TrainingManagement = () => {
  const [modules, setModules] = useState([]);
  const [expandedModule, setExpandedModule] = useState(null);
  const [lessons, setLessons] = useState({});
  const [loading, setLoading] = useState(true);
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);

  const [moduleForm, setModuleForm] = useState(EMPTY_MODULE);
  const [lessonForm, setLessonForm] = useState(EMPTY_LESSON);
  const [newDoc, setNewDoc] = useState({ name: "", url: "" });

  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/training/modules`, { withCredentials: true });
      setModules(response.data);
    } catch (error) {
      console.error("Failed to fetch modules:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLessons = useCallback(async (moduleId) => {
    try {
      const response = await axios.get(`${API}/training/modules/${moduleId}/lessons`, { withCredentials: true });
      setLessons(prev => ({ ...prev, [moduleId]: response.data }));
    } catch (error) {
      console.error(`Failed to fetch lessons for module ${moduleId}:`, error);
    }
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const toggleModule = (moduleId) => {
    if (expandedModule === moduleId) {
      setExpandedModule(null);
    } else {
      setExpandedModule(moduleId);
      if (!lessons[moduleId]) {
        fetchLessons(moduleId);
      }
    }
  };

  // Module CRUD
  const handleSaveModule = async (e) => {
    e.preventDefault();
    try {
      if (editingModule) {
        await axios.put(`${API}/training/modules/${editingModule.id}`, moduleForm, { withCredentials: true });
        toast.success("Module mis à jour");
      } else {
        await axios.post(`${API}/training/modules`, moduleForm, { withCredentials: true });
        toast.success("Module créé");
      }
      setModuleDialogOpen(false);
      setEditingModule(null);
      setModuleForm(EMPTY_MODULE);
      fetchModules();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({
      title: module.title,
      description: module.description,
      order: module.order,
      is_active: module.is_active
    });
    setModuleDialogOpen(true);
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm("Supprimer ce module et toutes ses leçons ?")) return;
    try {
      await axios.delete(`${API}/training/modules/${moduleId}`, { withCredentials: true });
      toast.success("Module supprimé");
      fetchModules();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Lesson CRUD
  const handleSaveLesson = async (e) => {
    e.preventDefault();
    try {
      if (editingLesson) {
        await axios.put(`${API}/training/lessons/${editingLesson.id}`, lessonForm, { withCredentials: true });
        toast.success("Leçon mise à jour");
      } else {
        await axios.post(`${API}/training/lessons`, lessonForm, { withCredentials: true });
        toast.success("Leçon créée");
      }
      setLessonDialogOpen(false);
      setEditingLesson(null);
      setLessonForm(EMPTY_LESSON);
      fetchLessons(selectedModuleId);
      fetchModules();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleAddLesson = (moduleId) => {
    setSelectedModuleId(moduleId);
    setLessonForm({ ...EMPTY_LESSON, module_id: moduleId });
    setLessonDialogOpen(true);
  };

  const handleEditLesson = (lesson, moduleId) => {
    setSelectedModuleId(moduleId);
    setEditingLesson(lesson);
    setLessonForm({
      module_id: lesson.module_id,
      title: lesson.title,
      description: lesson.description,
      video_url: lesson.video_url || "",
      documents: lesson.documents || [],
      order: lesson.order,
      is_active: lesson.is_active
    });
    setLessonDialogOpen(true);
  };

  const handleDeleteLesson = async (lessonId, moduleId) => {
    if (!window.confirm("Supprimer cette leçon ?")) return;
    try {
      await axios.delete(`${API}/training/lessons/${lessonId}`, { withCredentials: true });
      toast.success("Leçon supprimée");
      fetchLessons(moduleId);
      fetchModules();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const addDocument = () => {
    if (newDoc.name && newDoc.url) {
      setLessonForm({
        ...lessonForm,
        documents: [...lessonForm.documents, { ...newDoc }]
      });
      setNewDoc({ name: "", url: "" });
    }
  };

  const removeDocument = (docToRemove) => {
    setLessonForm({
      ...lessonForm,
      documents: lessonForm.documents.filter((d) => d.url !== docToRemove.url || d.name !== docToRemove.name)
    });
  };

  return (
    <div data-testid="training-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Outfit'] text-2xl font-bold uppercase">
          Gestion de la Formation
        </h1>
        <Dialog open={moduleDialogOpen} onOpenChange={(open) => {
          setModuleDialogOpen(open);
          if (!open) { setEditingModule(null); setModuleForm(EMPTY_MODULE); }
        }}>
          <DialogTrigger asChild>
            <button className="btn-primary flex items-center gap-2 py-3 px-6">
              <Plus className="w-5 h-5" />
              Nouveau module
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F0F16] border-white/10">
            <DialogHeader>
              <DialogTitle className="font-['Outfit'] text-xl uppercase">
                {editingModule ? "Modifier le module" : "Créer un module"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveModule} className="space-y-4 mt-4">
              <div>
                <Label className="form-label">Titre *</Label>
                <Input
                  value={moduleForm.title}
                  onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                  className="bg-[#05050A] border-white/10"
                  required
                />
              </div>
              <div>
                <Label className="form-label">Description *</Label>
                <Textarea
                  value={moduleForm.description}
                  onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                  className="bg-[#05050A] border-white/10"
                  rows={3}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Ordre</Label>
                  <Input
                    type="number"
                    value={moduleForm.order}
                    onChange={(e) => setModuleForm({ ...moduleForm, order: parseInt(e.target.value) })}
                    className="bg-[#05050A] border-white/10"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="module_active"
                    checked={moduleForm.is_active}
                    onChange={(e) => setModuleForm({ ...moduleForm, is_active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="module_active">Module actif</Label>
                </div>
              </div>
              <button type="submit" className="w-full btn-primary">
                {editingModule ? "Mettre à jour" : "Créer le module"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Lesson Dialog */}
      <Dialog open={lessonDialogOpen} onOpenChange={(open) => {
        setLessonDialogOpen(open);
        if (!open) {
          setEditingLesson(null);
          setLessonForm(EMPTY_LESSON);
        }
      }}>
        <DialogContent className="bg-[#0F0F16] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl uppercase">
              {editingLesson ? "Modifier la leçon" : "Ajouter une leçon"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveLesson} className="space-y-4 mt-4">
            <div>
              <Label className="form-label">Titre *</Label>
              <Input
                value={lessonForm.title}
                onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                className="bg-[#05050A] border-white/10"
                required
              />
            </div>
            <div>
              <Label className="form-label">Description *</Label>
              <Textarea
                value={lessonForm.description}
                onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                className="bg-[#05050A] border-white/10"
                rows={3}
                required
              />
            </div>
            <div>
              <Label className="form-label">URL de la vidéo</Label>
              <Input
                value={lessonForm.video_url}
                onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
                className="bg-[#05050A] border-white/10"
                placeholder="https://..."
              />
            </div>

            {/* Documents */}
            <div>
              <Label className="form-label">Documents à télécharger</Label>
              <div className="space-y-2 mb-3">
                {lessonForm.documents.map((doc) => (
                  <div key={`${doc.name}-${doc.url}`} className="flex items-center gap-2 bg-[#05050A] p-2 border border-white/10">
                    <File className="w-4 h-4 text-[#0066FF]" />
                    <span className="flex-1 text-sm">{doc.name}</span>
                    <button
                      type="button"
                      onClick={() => removeDocument(doc)}
                      className="text-red-500 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={newDoc.name}
                  onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
                  className="bg-[#05050A] border-white/10"
                  placeholder="Nom du document"
                />
                <Input
                  value={newDoc.url}
                  onChange={(e) => setNewDoc({ ...newDoc, url: e.target.value })}
                  className="bg-[#05050A] border-white/10"
                  placeholder="URL du document"
                />
              </div>
              <button
                type="button"
                onClick={addDocument}
                className="mt-2 text-[#0066FF] text-sm hover:text-[#3B82F6]"
              >
                + Ajouter un document
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Ordre</Label>
                <Input
                  type="number"
                  value={lessonForm.order}
                  onChange={(e) => setLessonForm({ ...lessonForm, order: parseInt(e.target.value) })}
                  className="bg-[#05050A] border-white/10"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="lesson_active"
                  checked={lessonForm.is_active}
                  onChange={(e) => setLessonForm({ ...lessonForm, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="lesson_active">Leçon active</Label>
              </div>
            </div>
            <button type="submit" className="w-full btn-primary">
              {editingLesson ? "Mettre à jour" : "Ajouter la leçon"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : modules.length === 0 ? (
        <div className="text-center py-12 text-[#94A3B8]">
          Aucun module de formation créé
        </div>
      ) : (
        <div className="space-y-4">
          {modules.map((module) => (
            <div key={module.id} className="bg-[#0F0F16] border border-white/10">
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                onClick={() => toggleModule(module.id)}
              >
                <div className="flex items-center gap-4">
                  {expandedModule === module.id ? (
                    <ChevronUp className="w-5 h-5 text-[#0066FF]" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                  <div>
                    <h3 className="font-semibold">{module.title}</h3>
                    <p className="text-[#94A3B8] text-sm">{module.lessons_count} leçon(s)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <span className={`status-badge ${module.is_active ? 'status-accepted' : 'status-rejected'}`}>
                    {module.is_active ? 'Actif' : 'Inactif'}
                  </span>
                  <button
                    onClick={() => handleEditModule(module)}
                    className="p-2 hover:bg-white/10 rounded"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteModule(module.id)}
                    className="p-2 hover:bg-red-500/20 rounded text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {expandedModule === module.id && (
                <div className="border-t border-white/10 p-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm text-[#94A3B8]">Leçons du module</span>
                    <button
                      onClick={() => handleAddLesson(module.id)}
                      className="text-[#0066FF] text-sm hover:text-[#3B82F6] flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter une leçon
                    </button>
                  </div>

                  {lessons[module.id]?.length === 0 ? (
                    <p className="text-[#64748B] text-sm">Aucune leçon dans ce module</p>
                  ) : (
                    <div className="space-y-2">
                      {lessons[module.id]?.map((lesson) => (
                        <div
                          key={lesson.id}
                          className="flex items-center justify-between p-3 bg-[#05050A] border border-white/5"
                        >
                          <div className="flex items-center gap-3">
                            {lesson.video_url ? (
                              <Video className="w-4 h-4 text-[#0066FF]" />
                            ) : (
                              <FileText className="w-4 h-4 text-[#94A3B8]" />
                            )}
                            <div>
                              <span className="text-sm">{lesson.title}</span>
                              {lesson.documents?.length > 0 && (
                                <span className="text-xs text-[#64748B] ml-2">
                                  ({lesson.documents.length} doc)
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs ${lesson.is_active ? 'text-green-500' : 'text-red-500'}`}>
                              {lesson.is_active ? '●' : '○'}
                            </span>
                            <button
                              onClick={() => handleEditLesson(lesson, module.id)}
                              className="p-1 hover:bg-white/10 rounded"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteLesson(lesson.id, module.id)}
                              className="p-1 hover:bg-red-500/20 rounded text-red-500"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrainingManagement;
